import type { ClampQcHubSettingsPayload } from "./clampQcSettingsApi";

/** Keep in sync with `clampQcInductanceReadingFlooredToHundredths` in clampqc-measurement-rules.ts */
function clampQcInductanceReadingFlooredToHundredths(inductance: number): number {
  return Math.floor(inductance * 100) / 100;
}

function clampInspectionSizeFromSku(sku: string): "large" | "small" | "unknown" {
  const trimmed = sku.trim();
  if (trimmed.startsWith("90-003")) {
    return "large";
  }
  if (trimmed.startsWith("90-002")) {
    return "small";
  }
  return "unknown";
}

/**
 * Returns the configured inclusive [min, max] for the job SKU line, or `null` when the SKU line is unknown.
 */
export function clampQcInductanceAcceptanceRangeForSku(
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): { min: number; max: number } | null {
  if (settings == null) {
    return null;
  }
  const line = clampInspectionSizeFromSku(sku);
  if (line === "large") {
    return { min: settings.inductanceMinLarge, max: settings.inductanceMaxLarge };
  }
  if (line === "small") {
    return { min: settings.inductanceMinSmall, max: settings.inductanceMaxSmall };
  }
  return null;
}

/** Parses a hub inductance field value after decimal sanitization; returns null when not a complete number. */
export function parseClampQcInductanceReading(inductanceRaw: string): number | null {
  const trimmed = inductanceRaw.trim();
  if (trimmed === "" || trimmed === "." || !/^\d*\.?\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number.parseFloat(trimmed);
  return Number.isFinite(value) ? value : null;
}

/** True when the field has a non-empty value that does not parse to a finite number. */
export function clampQcInductanceReadingHasInvalidNumeric(inductanceRaw: string): boolean {
  const trimmed = inductanceRaw.trim();
  if (trimmed === "") {
    return false;
  }
  return parseClampQcInductanceReading(inductanceRaw) === null;
}

/**
 * True when a finite inductance reading is outside the configured band for this SKU.
 * Invalid or empty input is not treated as out of band (use {@link clampQcInductanceEntryBlocksInspectionSubmit}).
 */
export function clampQcInductanceReadingIsOutOfBand(
  inductanceRaw: string,
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): boolean {
  const trimmed = inductanceRaw.trim();
  if (trimmed === "" || settings == null) {
    return false;
  }
  const value = parseClampQcInductanceReading(inductanceRaw);
  if (value === null) {
    return false;
  }
  const range = clampQcInductanceAcceptanceRangeForSku(sku, settings);
  if (range == null) {
    return false;
  }
  const floored = clampQcInductanceReadingFlooredToHundredths(value);
  return floored < range.min || floored > range.max;
}

/** @deprecated Use {@link clampQcInductanceReadingIsOutOfBand}. */
export function clampQcInductanceReadingFailsAcceptance(
  inductanceRaw: string,
  sku: string,
  settings: ClampQcHubSettingsPayload | null,
): boolean {
  return clampQcInductanceReadingIsOutOfBand(inductanceRaw, sku, settings);
}

/**
 * True while Record inspection must stay disabled until inductance input is corrected.
 * Only applies when this unit is in the stratified inductance sample; non-sampled units skip inductance entirely.
 */
export function clampQcInductanceEntryBlocksInspectionSubmit(
  inductanceRaw: string,
  inductanceSampled: boolean,
): boolean {
  if (!inductanceSampled) {
    return false;
  }
  const trimmed = inductanceRaw.trim();
  if (trimmed === "") {
    return true;
  }
  return clampQcInductanceReadingHasInvalidNumeric(inductanceRaw);
}
